/**
 * SHOP DESK SOLO — Pro, at $50, until the shop has 100 closed and paid jobs.
 *
 * Anthony, 2026-10-05: "keep existing plans as they are for shop desk pro.
 * add a third option. shop desk solo, lets you do a hundred customers in
 * pro version with everything for only $50 a month until you get a hundred
 * closed and paid jobs in it." And when it gets there: auto move to $149.
 *
 * ── WHAT A SOLO SHOP IS, IN THE DATABASE ───────────────────────────────
 *
 * plan 'paid' like any Pro shop, so every gate that switches Pro on keeps
 * working without knowing Solo exists. plan_tier 'solo' and plan_rate 50
 * are what make it Solo. Migration 0193.
 *
 * ── WHAT COUNTS ────────────────────────────────────────────────────────
 *
 * A job whose status is 'paid', which is only ever reached when its invoice
 * is settled (0025, 0101): closed AND paid, the words Anthony used. Training
 * jobs do not count. Only jobs CREATED on or after the day the shop went
 * Solo count, so a shop that ran free for a year, or imported its history,
 * is not stepped up the morning after it subscribes.
 *
 * ── THE STEP UP, AND WHY IT WAITS FOR A WINDOW ────────────────────────
 *
 * California's automatic renewal law (Bus. & Prof. Code 17602, as amended
 * by AB 2863, in force 1 July 2025) wants a price increase on a recurring
 * charge told to the customer at least 7 and no more than 30 days before
 * the charge that carries it. So reaching 100 does not move the price that
 * day. The daily run waits for the first day the next monthly bill is
 * between 7 and 30 days away, sends the notice, then moves the gateway's
 * amount. A bill is never more than 31 days away, so the wait is at most
 * about a week, and the shop is never charged $149 without the notice.
 *
 * Pure functions, no env and no database, so the dates are tested.
 */

/** $50.00 a month. routes/pro.ts charges this for Solo. */
export const SOLO_CENTS = 5_000;
/** What a Solo shop moves to: Pro's price. Kept here as a figure so the
 *  notice cannot quote a different number from the one the gateway gets. */
export const SOLO_STEPS_TO_CENTS = 14_900;
/** Closed and paid jobs on the desk before Solo becomes Pro's price. */
export const SOLO_JOB_CAP = 100;
/** The notice window, in days before the first charge at the new price. */
export const NOTICE_MIN_DAYS = 7;
export const NOTICE_MAX_DAYS = 30;

const DAY = 86_400_000;
const ms = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const iso = (t: number) => new Date(t).toISOString().slice(0, 10);

/**
 * The bill N months after the first charge. Same rule as oneMonthOn in
 * server/desk/pro-link.ts and the gateway's own: a day a month does not
 * have (the 31st) becomes that month's last day, and the NEXT month goes
 * back to the 31st rather than drifting to the 30th.
 */
export function monthsOn(firstCharge: string, n: number): string {
  const [y, m, d] = firstCharge.split("-").map(Number) as [number, number, number];
  const last = new Date(Date.UTC(y, m - 1 + n + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 1 + n, Math.min(d, last))).toISOString().slice(0, 10);
}

/** The next monthly bill strictly after `today`. The first charge was at checkout. */
export function nextBillAfter(firstCharge: string, today: string): string {
  for (let n = 1; n < 1200; n++) {
    const b = monthsOn(firstCharge, n);
    if (b > today) return b;
  }
  throw new Error(`no bill after ${today} from ${firstCharge}`);
}

export const daysBetween = (from: string, to: string): number => Math.round((ms(to) - ms(from)) / DAY);

export type SoloState = {
  /** plan_since: the day of the first charge. */
  since: string;
  /** Closed and paid jobs created since `since`. */
  paidJobs: number;
  /** solo_step_on, once the notice has gone. */
  stepOn: string | null;
};

export type StepVerdict =
  | { act: "under"; left: number }
  /** At the cap, but the next bill is too close (or too far) to give notice for. */
  | { act: "wait"; nextBill: string; days: number }
  /** Send the notice now; the bill on `stepOn` is the first at $149. */
  | { act: "notice"; stepOn: string; days: number }
  /** Notice given; the move happens on `stepOn`. */
  | { act: "noticed"; stepOn: string }
  /** `stepOn` has come: the shop is on Pro's price. Record it. */
  | { act: "stepped"; stepOn: string };

export function soloVerdict(s: SoloState, today: string): StepVerdict {
  if (s.stepOn) return today >= s.stepOn ? { act: "stepped", stepOn: s.stepOn } : { act: "noticed", stepOn: s.stepOn };
  const done = Math.max(0, Math.floor(Number(s.paidJobs) || 0));
  if (done < SOLO_JOB_CAP) return { act: "under", left: SOLO_JOB_CAP - done };
  const nextBill = nextBillAfter(s.since, today);
  const days = daysBetween(today, nextBill);
  return days >= NOTICE_MIN_DAYS && days <= NOTICE_MAX_DAYS
    ? { act: "notice", stepOn: nextBill, days }
    : { act: "wait", nextBill, days };
}

const dollars = (cents: number) => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;

/** "October 15, 2026". The notice is a legal notice, so the date is spelled out. */
export const longDay = (isoDay: string): string => new Date(`${isoDay}T12:00:00Z`).toLocaleDateString(
  "en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });

/**
 * What the shop agrees to at checkout. Shown beside the button and ticked
 * before the card is taken, because a price that changes later has to be
 * agreed to plainly at the start, not found in the terms afterwards.
 */
export const SOLO_TERMS =
  `Solo is ${dollars(SOLO_CENTS)} a month with everything in Pro, until your shop has ${SOLO_JOB_CAP} closed and paid `
  + `jobs on the desk. After that it moves to ${dollars(SOLO_STEPS_TO_CENTS)} a month, the Pro price, on the same card. `
  + `We email you between ${NOTICE_MIN_DAYS} and ${NOTICE_MAX_DAYS} days before the first ${dollars(SOLO_STEPS_TO_CENTS)} `
  + `charge, and you can cancel before it.`;

/** The notice, as plain text. The subject and body go out as one email. */
export function stepNotice(shop: string, stepOn: string, paidJobs: number): { subject: string; text: string } {
  const on = longDay(stepOn);
  return {
    subject: `${shop}: Shop Desk Solo moves to ${dollars(SOLO_STEPS_TO_CENTS)} a month on ${on}`,
    text: [
      `Hello,`,
      ``,
      `${shop} now has ${paidJobs} closed and paid jobs on Shop Desk. Congratulations.`,
      ``,
      `Solo covers the first ${SOLO_JOB_CAP}. As you agreed when you signed up, your plan now moves to `
        + `Shop Desk Pro at ${dollars(SOLO_STEPS_TO_CENTS)} a month. Nothing else changes. You already have everything in Pro.`,
      ``,
      `Your next charge, on ${on}, will be ${dollars(SOLO_STEPS_TO_CENTS)} instead of ${dollars(SOLO_CENTS)}, `
        + `on the same card, and every month after that until you cancel.`,
      ``,
      `If you do not want to continue, cancel before ${on} and you will not be charged again. To cancel, open `
        + `Plan and billing in Shop Desk and press Cancel. Or reply to this email or call (951) 904-2477, and we `
        + `will confirm it in writing.`,
      ``,
      `Anthony`,
      `Supercanic, Shop Desk`,
    ].join("\n"),
  };
}
